import './ai-analyst-page.css';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AIConversation from 'components/organisms/ai-chat/ai-conversation';
import ChatContainer from 'components/organisms/ai-chat/chat-container';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';
import { useChat } from 'store/useChatStore';
import useConversations from 'store/useConversationStore';
import { PrimaryRoutes } from 'common/enums/internal-route-enums';

interface ChatInputProps {}

export default function AIAnalystPage({}: ChatInputProps) {
	const { conversationId } = useParams<{ conversationId: string }>();
	const navigate = useNavigate();
	const { loadConversation, activeConversationId, messages } = useChat();
	const { selectConversation } = useConversations(false); // Don't auto-load conversations here
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [homepageInsights, setHomepageInsights] = useState<string | null>(null);
	const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
	const [isLoadingInsights, setIsLoadingInsights] = useState<boolean>(false);
	const [isNewChatClicked, setIsNewChatClicked] = useState<boolean>(false);
	const aiChatApiService = useAiChatApiService();
	const hasFetchedRef = useRef<boolean>(false);
	const hasLoadedConversationRef = useRef<string | null>(null);
	const previousConversationIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (messages.length > 0 && isNewChatClicked) {
			setIsNewChatClicked(false);
		}
	}, [messages.length, isNewChatClicked]);
	
	usePageTracking('Page Viewed', 'AI Analyst');

	// Load conversation from URL parameter on mount or when conversationId changes
	useEffect(() => {
		if (conversationId && conversationId !== hasLoadedConversationRef.current && conversationId !== activeConversationId) {
			hasLoadedConversationRef.current = conversationId;
			loadConversation(conversationId);
			selectConversation(conversationId); // Also set as active in sidebar
		}
	}, [conversationId, loadConversation, activeConversationId, selectConversation]);

	// Update URL when activeConversationId changes (e.g., after creating a new conversation)
	useEffect(() => {
		if (activeConversationId && activeConversationId !== conversationId && activeConversationId !== previousConversationIdRef.current) {
			previousConversationIdRef.current = activeConversationId;
			navigate(PrimaryRoutes.AIAnalystConversation.replace(':conversationId', activeConversationId), { replace: true });
		}
	}, [activeConversationId, conversationId, navigate]);

	useEffect(() => {
		if (hasFetchedRef.current) {
			return;
		}
		
		hasFetchedRef.current = true;
		
		const fetchHomepageInsights = async () => {
			setIsLoadingInsights(true);
			try {
				const response = await aiChatApiService.getHomepageInsights();
				if (response?.insights) {
					const insightsData = response.insights as any;
					if (insightsData?.text) {
						setHomepageInsights(insightsData.text);
					} else if (typeof insightsData === 'string') {
						setHomepageInsights(insightsData);
					}
				}
				if (response?.suggested_questions) {
					setSuggestedQuestions(response.suggested_questions);
				}
			} catch (error) {
				console.error('Error fetching homepage insights:', error);
			} finally {
				setIsLoadingInsights(false);
			}
		};

		fetchHomepageInsights();
	}, [aiChatApiService]);

	return (
		<div className="h-full flex flex-col bg-white relative">
			<div className="flex flex-1 overflow-hidden">
				<div className="md:flex hidden">
					<AIConversation 
						onSidebarCollapsedChange={setSidebarCollapsed}
						onNewChatClicked={setIsNewChatClicked}
					/>
				</div>
				<main
		  className={`
			flex-1 transition-all duration-200 
			${sidebarCollapsed ? 'md:w-[calc(100%-0px)]' : ''}
		  `}
		>
		  <ChatContainer 
			sidebarCollapsed={sidebarCollapsed} 
			homepageInsights={homepageInsights}
			suggestedQuestions={suggestedQuestions}
			isLoadingInsights={isLoadingInsights}
			isNewChatClicked={isNewChatClicked}
		  />
		</main>
			</div>
		</div>
	);
}
